import { useQuery } from "@tanstack/react-query";
import crmClient from "../../../lib/crm_client";

// Envelope is { success, message, data }; some endpoints nest once more under data.data.
const unwrap = (res) => {
  const d = res?.data?.data;
  return d && !Array.isArray(d) && d.data !== undefined ? d.data : d;
};

const num = (v) => Number(v) || 0;

const fetchDues = async (clientCode) => {
  const d = unwrap(
    await crmClient.get("/v1/company/getDuesData", { params: { clientCode } })
  );
  return {
    billedDues: num(d?.totalDuesClosingBalance),
    currentDues: num(d?.totalDuesBalance),
    unfixGold: num(d?.totalUnfixWeight),
  };
};

const fetchQuotes = async (clientCode) => {
  const d = unwrap(
    await crmClient.get("/v1/company/getAllQuotes2", { params: { clientCode } })
  );
  const row = (Array.isArray(d) ? d : []).find((i) => i?.label === "Pending quotes");
  return {
    bags: num(row?.bagQuantity),
    diamond: num(row?.totalDiaWeight),
    gold: num(row?.totalGoldWeight),
  };
};

const fetchOrders = async (clientCode) => {
  const d = unwrap(
    await crmClient.post("/v1/company/getClientAllOrderSummary3", {
      clientCode,
      orderStatus: "Pending",
      searchBy: "",
      sortBy: "orderDate",
      sortOrder: "desc",
    })
  );
  const row = (d?.orderStatusClientData || []).find((i) => i?.OrderNo === "All");
  return {
    bags: num(row?.pendingBags),
    diamond: num(row?.DieWt),
    gold: num(row?.NetWt),
  };
};

// The bag-status system answers 500 "Client detail doesn't exists" for a client
// it has never seen. That is "no WIP record", not a failure worth retrying.
const NO_WIP_RECORD = /client detail doesn'?t exist/i;

const fetchWip = async (clientCode) => {
  let d;
  try {
    d = unwrap(await crmClient.post("/v1/company/wipClient", { clientCode }));
  } catch (err) {
    if (NO_WIP_RECORD.test(err?.response?.data?.message || "")) {
      return { bags: 0, diamond: 0, gold: 0, noRecord: true };
    }
    throw err;
  }
  const t = d?.totalDepartmentGroupName;
  return {
    bags: num(t?.TotalBQty),
    diamond: num(t?.TotalRequiredDWt),
    gold: num(t?.TotalRequiredNWt),
  };
};

// Memo is keyed by client name, not code.
const fetchMemo = async (clientName) => {
  const d = unwrap(
    await crmClient.post("/v1/company/getClientMemoTotals", { clientName })
  );
  return {
    bags: num(d?.totalBagCount),
    diamond: num(d?.totalDiaWt),
    gold: num(d?.totalNetGoldWt),
  };
};

const fetchReady = async (clientCode) => {
  const d = unwrap(
    await crmClient.get("/v1/company/readyForSell/getReadyForSellingData", {
      params: { clientCode },
    })
  );
  return {
    articles: num(d?.totalBags),
    diamond: num(d?.totalDiaWt),
    gold: num(d?.totalNetWt),
  };
};

// No automatic retry: a slow ERP call would keep the shimmer up for twice as
// long. A failed section shows "-" and is retried the next time the tray opens.
const opts = { retry: false, staleTime: 2 * 60 * 1000 };

// Each section is its own query so one failing endpoint doesn't blank the rest.
export function useSynopsis({ clientCode, clientName, enabled }) {
  const on = !!enabled && !!clientCode;
  return {
    dues: useQuery({ queryKey: ["synopsis", "dues", clientCode], queryFn: () => fetchDues(clientCode), enabled: on, ...opts }),
    quotes: useQuery({ queryKey: ["synopsis", "quotes", clientCode], queryFn: () => fetchQuotes(clientCode), enabled: on, ...opts }),
    orders: useQuery({ queryKey: ["synopsis", "orders", clientCode], queryFn: () => fetchOrders(clientCode), enabled: on, ...opts }),
    wip: useQuery({ queryKey: ["synopsis", "wip", clientCode], queryFn: () => fetchWip(clientCode), enabled: on, ...opts }),
    memo: useQuery({ queryKey: ["synopsis", "memo", clientName], queryFn: () => fetchMemo(clientName), enabled: on && !!clientName, ...opts }),
    ready: useQuery({ queryKey: ["synopsis", "ready", clientCode], queryFn: () => fetchReady(clientCode), enabled: on, ...opts }),
  };
}
